import React from 'react';

const formatValue = (value) => {
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'Aucun';
  return value || 'Non défini';
};

const hasChanged = (before, after) => {
  if (Array.isArray(before) || Array.isArray(after)) {
    return JSON.stringify(before || []) !== JSON.stringify(after || []);
  }
  return (after || '') !== (before || '');
};

export const ProposalDiff = ({ original, proposed, action, circleLabel, operations = [] }) => {
  if (!proposed && !operations.length) return null;

  if (operations && operations.length > 0) {
    return (
      <div className="space-y-2 mt-2">
        <p className="text-xs uppercase text-slate-500 font-semibold">Operations</p>
        {operations.map((op, idx) => {
          const roleName =
            op.target_circle_id === null
              ? 'Nouveau role'
              : (op.target_circle_id && circleLabel) || `Role #${op.target_circle_id || ''}`;
          return (
            <div key={idx} className="border border-slate-200 rounded-lg p-3 bg-slate-50 text-xs space-y-1">
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 font-semibold uppercase text-[11px]">
                  {op.action || 'edit'}
                </span>
                <span className="text-slate-800 font-semibold">{roleName}</span>
              </div>
              {op.data && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-slate-700">
                  <div>
                    <p className="text-[11px] uppercase text-slate-500">Nom</p>
                    <p className="font-medium">{op.data.name || '—'}</p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase text-slate-500">Raison d'etre</p>
                    <p className="font-medium">{op.data.purpose || '—'}</p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase text-slate-500">Domaines</p>
                    <p className="font-medium">{formatValue(op.data.domains || [])}</p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase text-slate-500">Redevabilites</p>
                    <p className="font-medium">{formatValue(op.data.accountabilities || [])}</p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  const fields = [
    { key: 'name', label: 'Nom' },
    { key: 'purpose', label: "Raison d'etre" },
    { key: 'domains', label: 'Domaines' },
    { key: 'accountabilities', label: 'Redevabilites' },
  ];

  if (action === 'delete') {
    return (
      <div className="mt-2 p-3 border border-red-100 bg-red-50 rounded-lg text-xs text-red-700 space-y-1">
        <p className="text-sm font-semibold text-red-800">Suppression du role</p>
        <p>Ce vote propose de supprimer le role {circleLabel || 'cible'}.</p>
        {original && (
          <p className="text-slate-600 text-[11px]">
            Raison d'etre actuelle : {formatValue(original.purpose)}
          </p>
        )}
      </div>
    );
  }

  if (action === 'create') {
    return (
      <div className="mt-2 border border-emerald-100 bg-emerald-50 rounded-lg p-3 text-xs text-slate-800 space-y-1">
        <p className="text-sm font-semibold text-emerald-800">Création d'un nouveau role</p>
        {fields.map(({ key, label }) => (
          <p key={key}>
            <span className="font-semibold">{label} :</span> {formatValue(proposed[key])}
          </p>
        ))}
      </div>
    );
  }

  return (
    <div className="mt-2 text-xs text-slate-700 space-y-2">
      <div className="flex flex-wrap gap-2 text-[11px] font-semibold uppercase text-slate-600">
        {circleLabel ? (
          <span className="bg-slate-100 text-slate-700 px-2 py-1 rounded-full">Role actuel : {circleLabel}</span>
        ) : (
          <span className="bg-amber-100 text-amber-800 px-2 py-1 rounded-full">Role actuel introuvable</span>
        )}
        <span className="bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full">Proposition</span>
      </div>
      <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 overflow-hidden">
        {fields.map(({ key, label }) => {
          const previousValue =
            key === 'domains'
              ? original?.domains || []
              : key === 'accountabilities'
                ? original?.accountabilities || []
                : key === 'purpose'
                  ? original?.purpose || ''
                  : original?.name || '';
          const proposedValue = proposed[key];
          const changed = hasChanged(previousValue, proposedValue);
          return (
            <div key={key} className={`px-3 py-2 ${changed ? 'bg-amber-50' : 'bg-white'}`}>
              <p className="text-[11px] uppercase text-slate-500">{label}</p>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                <span className="text-slate-600">Avant : {formatValue(previousValue)}</span>
                <span className="text-slate-900 font-semibold">Apres : {formatValue(proposedValue)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const VoteDetails = ({ vote, circles }) => {
  if (!vote) return null;
  const proposed = vote.proposed_data || {};
  const currentCircle = vote.circle_id ? circles.find((c) => c.id === vote.circle_id) : null;
  const fallbackAction = proposed.delete ? 'delete' : !vote.circle_id ? 'create' : 'edit';
  const action = vote.tension_action || fallbackAction;
  const original = action === 'create'
    ? null
    : currentCircle
      ? {
          name: currentCircle.name,
          purpose: currentCircle.purpose,
          domains: currentCircle.domains || [],
          accountabilities: currentCircle.accountabilities || [],
        }
      : null;

  return (
    <ProposalDiff
      original={original}
      proposed={proposed}
      action={action}
      circleLabel={currentCircle?.name || vote.tension_title}
    />
  );
};

export default ProposalDiff;
